/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { z, ZodSchema } from 'zod';
import { DatabaseService } from './DatabaseService';
import { PersistenceService } from './PersistenceService';
import { getDb } from '../db/connection';
import { 
  compressData, 
  decompressData, 
  compareVersions,
  createDataSnapshot,
  validateDataIntegrity,
  transformDataStructure,
  MigrationSchema,
  BackupManifest,
  MigrationHistory,
  DataVersion
} from '../utils/dataMigration';

// Migration configuration types
export interface MigrationConfig {
  maxBackups: number;
  compressionEnabled: boolean;
  validationEnabled: boolean;
  progressiveChunkSize: number;
  autoRollback: boolean;
  retryAttempts: number;
  backupRetentionDays: number;
}

// Migration operation types
export interface MigrationOperation {
  id: string;
  version: DataVersion;
  targetVersion: DataVersion;
  type: 'upgrade' | 'downgrade' | 'transform';
  status: 'pending' | 'in-progress' | 'completed' | 'failed' | 'rolled-back';
  startTime?: Date;
  endTime?: Date;
  error?: Error;
  affectedRecords: number;
  rollbackAvailable: boolean;
}

// Migration result
export interface MigrationResult {
  success: boolean;
  operationId: string;
  fromVersion: DataVersion;
  toVersion: DataVersion;
  duration: number;
  recordsProcessed: number;
  errors: MigrationError[];
  warnings: string[];
  backupId?: string;
}

// Migration error
export interface MigrationError {
  code: string;
  message: string;
  recordId?: string;
  field?: string;
  value?: unknown;
  timestamp: Date;
}

// Progressive migration state
export interface ProgressiveMigrationState {
  operationId: string;
  totalRecords: number;
  processedRecords: number;
  currentChunk: number;
  totalChunks: number;
  lastProcessedId?: string;
  checkpoints: MigrationCheckpoint[];
}

// Migration checkpoint
export interface MigrationCheckpoint {
  chunkIndex: number;
  recordIds: string[];
  timestamp: Date;
  success: boolean;
}

// Default configuration
const DEFAULT_CONFIG: MigrationConfig = {
  maxBackups: 10,
  compressionEnabled: true,
  validationEnabled: true,
  progressiveChunkSize: 100,
  autoRollback: true,
  retryAttempts: 3,
  backupRetentionDays: 30,
};

export class DataMigrationService {
  private static instance: DataMigrationService;
  private config: MigrationConfig;
  private persistenceService: PersistenceService;
  private migrationSchemas: Map<string, MigrationSchema[]> = new Map();
  private activeMigrations: Map<string, MigrationOperation> = new Map();
  private migrationHistory: MigrationHistory[] = [];
  private backupManifests: Map<string, BackupManifest> = new Map();
  private progressiveStates: Map<string, ProgressiveMigrationState> = new Map();

  private constructor(config: Partial<MigrationConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.persistenceService = PersistenceService.getInstance();
    // Initialize the store asynchronously to avoid blocking constructor
    this.initializeMigrationStore().catch(error => {
      console.error('Failed to initialize migration store:', error);
    });
  }

  static getInstance(config?: Partial<MigrationConfig>): DataMigrationService {
    if (!DataMigrationService.instance) {
      DataMigrationService.instance = new DataMigrationService(config);
    }
    return DataMigrationService.instance;
  }

  // Initialize migration store in IndexedDB
  private async initializeMigrationStore(): Promise<void> {
    try {
      const db = await getDb();
      
      if (!db) {
        console.warn('Database not available, skipping migration store initialization');
        return;
      }
      
      const version = db.version;
      
      if (!db.objectStoreNames.contains('migrations')) {
        db.close();
        
        const { openDB } = await import('idb');
        await openDB('phoneverse', version + 1, {
          upgrade(database) {
            if (!database.objectStoreNames.contains('migrations')) {
              const migrationStore = database.createObjectStore('migrations', { keyPath: 'id' });
              migrationStore.createIndex('version', 'version');
              migrationStore.createIndex('timestamp', 'timestamp');
              migrationStore.createIndex('type', 'type');
            }
            
            if (!database.objectStoreNames.contains('backups')) {
              const backupStore = database.createObjectStore('backups', { keyPath: 'id' });
              backupStore.createIndex('timestamp', 'timestamp');
              backupStore.createIndex('dataType', 'dataType');
            }
          }
        });
      }
      
      // Load migration history and backup manifests
      await this.loadMigrationHistory();
      await this.loadBackupManifests();
    } catch (error) {
      console.error('Failed to initialize migration store:', error);
    }
  }

  // Register migration schemas for a data type
  registerMigrationSchemas(dataType: string, schemas: MigrationSchema[]): void {
    this.migrationSchemas.set(dataType, schemas.sort((a, b) => 
      compareVersions(a.version, b.version)
    ));
  }

  // Main migration method
  async migrate(
    dataType: string,
    targetVersion: DataVersion,
    options?: {
      createBackup?: boolean;
      progressive?: boolean;
      validateBefore?: boolean;
      validateAfter?: boolean;
    }
  ): Promise<MigrationResult> {
    const startTime = performance.now();
    const operationId = this.generateOperationId();
    const errors: MigrationError[] = [];
    const warnings: string[] = [];
    
    try {
      // Get current version
      const currentVersion = await this.getCurrentVersion(dataType);
      
      if (compareVersions(currentVersion, targetVersion) === 0) {
        return {
          success: true,
          operationId,
          fromVersion: currentVersion,
          toVersion: targetVersion,
          duration: 0,
          recordsProcessed: 0,
          errors: [],
          warnings: ['Already at target version'],
        };
      }

      // Create backup if requested
      let backupId: string | undefined;
      if (options?.createBackup !== false) {
        backupId = await this.createBackup(dataType, currentVersion);
      }

      // Create migration operation
      const operation: MigrationOperation = {
        id: operationId,
        version: currentVersion,
        targetVersion,
        type: compareVersions(currentVersion, targetVersion) < 0 ? 'upgrade' : 'downgrade',
        status: 'pending',
        affectedRecords: 0,
        rollbackAvailable: !!backupId,
      };

      this.activeMigrations.set(operationId, operation);

      // Get migration path
      const migrationPath = this.getMigrationPath(dataType, currentVersion, targetVersion);
      
      if (migrationPath.length === 0) {
        throw new Error(`No migration path found from ${currentVersion.major}.${currentVersion.minor}.${currentVersion.patch} to ${targetVersion.major}.${targetVersion.minor}.${targetVersion.patch}`);
      }

      // Update operation status
      operation.status = 'in-progress';
      operation.startTime = new Date();

      // Load all data for migration
      const allData = await this.loadDataForMigration(dataType);
      
      if (options?.validateBefore !== false) {
        const validationErrors = await this.validateData(dataType, allData, currentVersion);
        errors.push(...validationErrors);
        
        if (validationErrors.length > 0 && this.config.validationEnabled) {
          throw new Error('Pre-migration validation failed');
        }
      }

      // Execute migrations
      let migratedData = allData;
      let processedRecords = 0;

      if (options?.progressive) {
        const result = await this.executeProgressiveMigration(
          operationId,
          dataType,
          migrationPath,
          migratedData
        );
        migratedData = result.data;
        processedRecords = result.processedRecords;
      } else {
        for (const migration of migrationPath) {
          const result = await this.executeMigration(
            migration,
            migratedData,
            operation.type === 'upgrade'
          );
          migratedData = result.data;
          processedRecords += result.recordsProcessed;
        }
      }

      // Validate after migration
      if (options?.validateAfter !== false) {
        const validationErrors = await this.validateData(dataType, migratedData, targetVersion);
        errors.push(...validationErrors);
        
        if (validationErrors.length > 0 && this.config.validationEnabled) {
          throw new Error('Post-migration validation failed');
        }
      }

      // Save migrated data
      await this.saveDataAfterMigration(dataType, migratedData, targetVersion);

      // Update operation status
      operation.status = 'completed';
      operation.endTime = new Date();
      operation.affectedRecords = processedRecords;

      // Record migration history
      await this.recordMigrationHistory({
        id: operationId,
        dataType,
        fromVersion: currentVersion,
        toVersion: targetVersion,
        timestamp: new Date(),
        success: true,
        recordsProcessed: processedRecords,
        duration: performance.now() - startTime,
        backupId,
      });

      return {
        success: true,
        operationId,
        fromVersion: currentVersion,
        toVersion: targetVersion,
        duration: performance.now() - startTime,
        recordsProcessed: processedRecords,
        errors,
        warnings,
        backupId,
      };

    } catch (error) {
      const operation = this.activeMigrations.get(operationId);
      
      if (operation) {
        operation.status = 'failed';
        operation.endTime = new Date();
        operation.error = error as Error;
      }

      // Attempt rollback if enabled
      if (this.config.autoRollback && operation?.rollbackAvailable) {
        try {
          await this.rollback(operationId);
          warnings.push('Migration failed and was rolled back');
        } catch (rollbackError) {
          errors.push({
            code: 'ROLLBACK_FAILED',
            message: `Rollback failed: ${rollbackError.message}`,
            timestamp: new Date(),
          });
        }
      }

      return {
        success: false,
        operationId,
        fromVersion: await this.getCurrentVersion(dataType),
        toVersion: targetVersion,
        duration: performance.now() - startTime,
        recordsProcessed: 0,
        errors: [...errors, {
          code: 'MIGRATION_FAILED',
          message: error.message,
          timestamp: new Date(),
        }],
        warnings,
      };

    } finally {
      this.activeMigrations.delete(operationId);
      this.progressiveStates.delete(operationId);
    }
  }

  // Create backup
  async createBackup(
    dataType: string,
    version: DataVersion,
    options?: {
      compress?: boolean;
      metadata?: Record<string, unknown>;
    }
  ): Promise<string> {
    const backupId = this.generateBackupId();
    const data = await this.loadDataForMigration(dataType);
    
    const snapshot = createDataSnapshot(data, {
      id: backupId,
      dataType,
      version,
      timestamp: new Date(),
      metadata: options?.metadata,
    });

    let backupData: string | DataSnapshot = snapshot;
    
    if (options?.compress !== false && this.config.compressionEnabled) {
      backupData = await compressData(snapshot);
    }

    // Save backup to IndexedDB
    await DatabaseService.add('backups', {
      id: backupId,
      dataType,
      version,
      timestamp: new Date(),
      data: backupData,
      compressed: options?.compress !== false && this.config.compressionEnabled,
      size: JSON.stringify(backupData).length,
      metadata: options?.metadata,
    });

    // Update backup manifest
    const manifest: BackupManifest = {
      id: backupId,
      dataType,
      version,
      timestamp: new Date(),
      size: JSON.stringify(backupData).length,
      recordCount: Array.isArray(data) ? data.length : Object.keys(data).length,
      compressed: options?.compress !== false && this.config.compressionEnabled,
      metadata: options?.metadata,
    };

    this.backupManifests.set(backupId, manifest);
    await this.saveBackupManifest(manifest);

    // Clean up old backups
    await this.cleanupOldBackups(dataType);

    return backupId;
  }

  // Restore from backup
  async restoreFromBackup(backupId: string): Promise<boolean> {
    try {
      const backup = await DatabaseService.get('backups', backupId);
      
      if (!backup) {
        throw new Error(`Backup ${backupId} not found`);
      }

      let data = backup.data;
      
      if (backup.compressed) {
        data = await decompressData(data);
      }

      // Extract original data from snapshot
      const snapshot = data as { data: unknown; metadata: Record<string, unknown> };
      
      // Restore data
      await this.saveDataAfterMigration(
        backup.dataType,
        snapshot.data,
        backup.version
      );

      return true;
    } catch (error) {
      console.error('Failed to restore from backup:', error);
      return false;
    }
  }

  // Rollback migration
  async rollback(operationId: string): Promise<boolean> {
    const operation = this.activeMigrations.get(operationId);
    
    if (!operation || !operation.rollbackAvailable) {
      return false;
    }

    try {
      const history = this.migrationHistory.find(h => h.id === operationId);
      
      if (history && history.backupId) {
        const success = await this.restoreFromBackup(history.backupId);
        
        if (success) {
          operation.status = 'rolled-back';
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Rollback failed:', error);
      return false;
    }
  }

  // Progressive migration execution
  private async executeProgressiveMigration(
    operationId: string,
    dataType: string,
    migrationPath: MigrationSchema[],
    data: unknown
  ): Promise<{ data: unknown; processedRecords: number }> {
    const records = Array.isArray(data) ? data : Object.values(data as Record<string, unknown>);
    const totalRecords = records.length;
    const chunkSize = this.config.progressiveChunkSize;
    const totalChunks = Math.ceil(totalRecords / chunkSize);
    
    const state: ProgressiveMigrationState = {
      operationId,
      totalRecords,
      processedRecords: 0,
      currentChunk: 0,
      totalChunks,
      checkpoints: [],
    };

    this.progressiveStates.set(operationId, state);

    let migratedRecords: unknown[] = [];
    
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, totalRecords);
      const chunk = records.slice(start, end);
      
      state.currentChunk = i + 1;

      let migratedChunk = chunk;
      
      for (const migration of migrationPath) {
        const result = await this.executeMigration(
          migration,
          migratedChunk,
          true
        );
        migratedChunk = result.data as unknown[];
      }

      migratedRecords = migratedRecords.concat(migratedChunk);
      state.processedRecords += chunk.length;

      // Create checkpoint
      const checkpoint: MigrationCheckpoint = {
        chunkIndex: i,
        recordIds: chunk.map((r: any) => r.id || `record-${start + chunk.indexOf(r)}`),
        timestamp: new Date(),
        success: true,
      };

      state.checkpoints.push(checkpoint);

      // Allow other operations to process
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    return {
      data: Array.isArray(data) ? migratedRecords : this.arrayToObject(migratedRecords),
      processedRecords: state.processedRecords,
    };
  }

  // Execute single migration
  private async executeMigration(
    migration: MigrationSchema,
    data: unknown,
    isUpgrade: boolean
  ): Promise<{ data: unknown; recordsProcessed: number }> {
    let result: unknown;
    let recordsProcessed = 0;

    if (isUpgrade) {
      result = await migration.up(data);
    } else {
      result = await migration.down(data);
    }

    if (Array.isArray(data)) {
      recordsProcessed = (data as unknown[]).length;
    } else if (typeof data === 'object' && data !== null) {
      recordsProcessed = Object.keys(data).length;
    }

    // Validate result with schema if provided
    if (migration.schema) {
      const validationResult = migration.schema.safeParse(result);
      
      if (!validationResult.success) {
        throw new Error(`Migration validation failed: ${validationResult.error.message}`);
      }
      
      result = validationResult.data;
    }

    return { data: result, recordsProcessed };
  }

  // Get migration path between versions
  private getMigrationPath(
    dataType: string,
    fromVersion: DataVersion,
    toVersion: DataVersion
  ): MigrationSchema[] {
    const schemas = this.migrationSchemas.get(dataType) || [];
    const path: MigrationSchema[] = [];
    
    const isUpgrade = compareVersions(fromVersion, toVersion) < 0;
    
    for (const schema of schemas) {
      const schemaComparison = compareVersions(schema.version, fromVersion);
      const targetComparison = compareVersions(schema.version, toVersion);
      
      if (isUpgrade) {
        if (schemaComparison > 0 && targetComparison <= 0) {
          path.push(schema);
        }
      } else {
        if (schemaComparison <= 0 && targetComparison > 0) {
          path.unshift(schema);
        }
      }
    }

    return path;
  }

  // Validate data
  private async validateData(
    dataType: string,
    data: unknown,
    version: DataVersion
  ): Promise<MigrationError[]> {
    const errors: MigrationError[] = [];
    
    try {
      const isValid = await validateDataIntegrity(data, {
        checkRequired: true,
        checkTypes: true,
        checkReferences: true,
      });

      if (!isValid) {
        errors.push({
          code: 'VALIDATION_FAILED',
          message: 'Data integrity validation failed',
          timestamp: new Date(),
        });
      }
    } catch (error) {
      errors.push({
        code: 'VALIDATION_ERROR',
        message: error.message,
        timestamp: new Date(),
      });
    }

    return errors;
  }

  // Version management
  private async getCurrentVersion(dataType: string): Promise<DataVersion> {
    try {
      const versionRecord = await DatabaseService.get('migrations', `version-${dataType}`);
      
      if (versionRecord) {
        return versionRecord.version;
      }
      
      return { major: 1, minor: 0, patch: 0 };
    } catch {
      return { major: 1, minor: 0, patch: 0 };
    }
  }

  private async setCurrentVersion(dataType: string, version: DataVersion): Promise<void> {
    const versionId = `version-${dataType}`;
    const existing = await DatabaseService.get('migrations', versionId);
    
    if (existing) {
      await DatabaseService.update('migrations', versionId, { version });
    } else {
      await DatabaseService.add('migrations', {
        id: versionId,
        dataType,
        version,
        timestamp: new Date(),
      });
    }
  }

  // Data loading and saving
  private async loadDataForMigration(dataType: string): Promise<unknown> {
    // Try to load from IndexedDB first
    const indexedData = await this.persistenceService.loadFromIndexedDB(dataType, {
      validate: false,
      migrate: false,
    });

    if (indexedData) {
      return indexedData;
    }

    // Fall back to localStorage
    const localData = await this.persistenceService.loadFromLocalStorage(dataType, {
      validate: false,
      migrate: false,
    });

    return localData || [];
  }

  private async saveDataAfterMigration(
    dataType: string,
    data: unknown,
    version: DataVersion
  ): Promise<void> {
    // Determine storage based on routing
    const routing = ['products', 'inventory', 'production', 'market', 'company'];
    
    if (routing.includes(dataType)) {
      await this.persistenceService.syncToIndexedDB(dataType, data, {
        validate: false,
        debounce: false,
      });
    } else {
      await this.persistenceService.syncToLocalStorage(dataType, data, {
        validate: false,
        debounce: false,
      });
    }

    // Update version
    await this.setCurrentVersion(dataType, version);
  }

  // History management
  private async loadMigrationHistory(): Promise<void> {
    try {
      const history = await DatabaseService.getAll('migrations');
      this.migrationHistory = history
        .filter((h: any) => h.type === 'history')
        .map((h: any) => h as MigrationHistory);
    } catch (error: any) {
      // Handle case where migrations object store doesn't exist yet
      if (error?.name === 'NotFoundError' || 
          error?.message?.includes('object store') ||
          error?.message?.includes('database connection is closing')) {
        console.warn('Migrations store not available yet. This is normal on first run or after database upgrade.');
        this.migrationHistory = [];
        return;
      }
      console.error('Failed to load migration history:', error);
    }
  }

  private async recordMigrationHistory(history: MigrationHistory): Promise<void> {
    this.migrationHistory.push(history);
    
    try {
      await DatabaseService.add('migrations', {
        ...history,
        type: 'history',
      });
    } catch (error: any) {
      // Handle case where migrations object store doesn't exist yet
      if (error?.name === 'NotFoundError' || 
          error?.message?.includes('object store') ||
          error?.message?.includes('database connection is closing')) {
        console.warn('Unable to persist migration history. Migrations store not available yet.');
        // Continue without persisting - the migration still succeeded
      } else {
        console.error('Failed to record migration history:', error);
        // Don't throw - we don't want to fail the migration just because we couldn't record history
      }
    }

    // Keep only recent history
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 90);
    
    this.migrationHistory = this.migrationHistory.filter(
      h => h.timestamp > cutoffDate
    );
  }

  // Backup management
  private async loadBackupManifests(): Promise<void> {
    try {
      const backups = await DatabaseService.getAll('backups');
      
      for (const backup of backups) {
        const manifest: BackupManifest = {
          id: backup.id,
          dataType: backup.dataType,
          version: backup.version,
          timestamp: new Date(backup.timestamp),
          size: backup.size,
          recordCount: backup.recordCount || 0,
          compressed: backup.compressed,
          metadata: backup.metadata,
        };
        
        this.backupManifests.set(backup.id, manifest);
      }
    } catch (error: any) {
      // Handle case where backups object store doesn't exist
      if (error?.name === 'NotFoundError' || 
          error?.message?.includes('object store') ||
          error?.message?.includes('database connection is closing')) {
        console.warn('Backups store not available yet. This is normal on first run or after database upgrade.');
        this.backupManifests.clear();
        return;
      }
      console.error('Failed to load backup manifests:', error);
    }
  }

  private async saveBackupManifest(manifest: BackupManifest): Promise<void> {
    await DatabaseService.update('backups', manifest.id, manifest);
  }

  private async cleanupOldBackups(dataType: string): Promise<void> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - this.config.backupRetentionDays);

    const backupsToDelete: string[] = [];
    
    for (const [id, manifest] of this.backupManifests) {
      if (manifest.dataType === dataType && manifest.timestamp < cutoffDate) {
        backupsToDelete.push(id);
      }
    }

    // Keep at least the configured number of backups
    const remainingBackups = Array.from(this.backupManifests.values())
      .filter(m => m.dataType === dataType)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    const toKeep = remainingBackups.slice(0, this.config.maxBackups);
    const toDelete = remainingBackups.slice(this.config.maxBackups);

    for (const backup of toDelete) {
      if (!backupsToDelete.includes(backup.id)) {
        backupsToDelete.push(backup.id);
      }
    }

    // Delete old backups
    for (const id of backupsToDelete) {
      await DatabaseService.delete('backups', id);
      this.backupManifests.delete(id);
    }
  }

  // Utility methods
  private generateOperationId(): string {
    return `mig-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private generateBackupId(): string {
    return `bkp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private arrayToObject(array: unknown[]): Record<string, unknown> {
    const obj: Record<string, unknown> = {};
    
    for (const item of array) {
      if (typeof item === 'object' && item !== null && 'id' in item) {
        obj[(item as any).id] = item;
      }
    }
    
    return obj;
  }

  // Public API methods
  async getBackupManifests(dataType?: string): Promise<BackupManifest[]> {
    const manifests = Array.from(this.backupManifests.values());
    
    if (dataType) {
      return manifests.filter(m => m.dataType === dataType);
    }
    
    return manifests;
  }

  async getMigrationHistory(dataType?: string): Promise<MigrationHistory[]> {
    if (dataType) {
      return this.migrationHistory.filter(h => h.dataType === dataType);
    }
    
    return this.migrationHistory;
  }

  getActiveMigrations(): MigrationOperation[] {
    return Array.from(this.activeMigrations.values());
  }

  getProgressiveState(operationId: string): ProgressiveMigrationState | undefined {
    return this.progressiveStates.get(operationId);
  }

  // Export and import functionality
  async exportData(dataType: string, format: 'json' | 'csv' = 'json'): Promise<Blob> {
    const data = await this.loadDataForMigration(dataType);
    const version = await this.getCurrentVersion(dataType);
    
    const exportData = {
      dataType,
      version,
      timestamp: new Date(),
      data,
    };

    if (format === 'json') {
      const json = JSON.stringify(exportData, null, 2);
      return new Blob([json], { type: 'application/json' });
    } else {
      // CSV export (simplified for arrays)
      if (Array.isArray(data)) {
        const csv = this.convertToCSV(data);
        return new Blob([csv], { type: 'text/csv' });
      } else {
        throw new Error('CSV export only supported for array data');
      }
    }
  }

  async importData(
    dataType: string,
    file: File,
    options?: {
      validate?: boolean;
      createBackup?: boolean;
    }
  ): Promise<MigrationResult> {
    try {
      const text = await file.text();
      let importData: any;
      
      if (file.type === 'application/json') {
        importData = JSON.parse(text);
      } else if (file.type === 'text/csv') {
        importData = this.parseCSV(text);
      } else {
        throw new Error('Unsupported file format');
      }

      // Create backup before import
      if (options?.createBackup !== false) {
        await this.createBackup(dataType, await this.getCurrentVersion(dataType));
      }

      // Validate if requested
      if (options?.validate !== false) {
        const errors = await this.validateData(dataType, importData.data, importData.version);
        
        if (errors.length > 0) {
          throw new Error('Import validation failed');
        }
      }

      // Save imported data
      await this.saveDataAfterMigration(dataType, importData.data, importData.version);

      return {
        success: true,
        operationId: this.generateOperationId(),
        fromVersion: await this.getCurrentVersion(dataType),
        toVersion: importData.version,
        duration: 0,
        recordsProcessed: Array.isArray(importData.data) ? importData.data.length : 1,
        errors: [],
        warnings: [],
      };
    } catch (error) {
      return {
        success: false,
        operationId: this.generateOperationId(),
        fromVersion: await this.getCurrentVersion(dataType),
        toVersion: { major: 0, minor: 0, patch: 0 },
        duration: 0,
        recordsProcessed: 0,
        errors: [{
          code: 'IMPORT_FAILED',
          message: error.message,
          timestamp: new Date(),
        }],
        warnings: [],
      };
    }
  }

  private convertToCSV(data: unknown[]): string {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0] as object);
    const rows = data.map(item => 
      headers.map(header => JSON.stringify((item as any)[header] || ''))
    );
    
    return [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
  }

  private parseCSV(text: string): any {
    const lines = text.split('\n');
    const headers = lines[0].split(',');
    
    const data = lines.slice(1).map(line => {
      const values = line.split(',');
      const obj: any = {};
      
      headers.forEach((header, index) => {
        try {
          obj[header] = JSON.parse(values[index]);
        } catch {
          obj[header] = values[index];
        }
      });
      
      return obj;
    });
    
    return { data, version: { major: 1, minor: 0, patch: 0 } };
  }

  // Cleanup
  destroy(): void {
    this.activeMigrations.clear();
    this.progressiveStates.clear();
    this.migrationSchemas.clear();
    this.backupManifests.clear();
    this.migrationHistory = [];
    DataMigrationService.instance = null as any;
  }
}

// Export singleton instance getter
export const getDataMigrationService = (
  config?: Partial<MigrationConfig>
): DataMigrationService => {
  return DataMigrationService.getInstance(config);
};